import { useNavigate } from 'react-router'

import { routes } from '@/app/routes'
import { useTranslate } from '@/shared/i18n'
import type { TranslationKey } from '@/shared/i18n'
import { Button, Card, Icon, Stack, Text } from '@/shared/ui'

const NEXT: { icon: string; title: TranslationKey; body: TranslationKey }[] = [
  { icon: '⏳', title: 'home.first.deadlines', body: 'home.first.deadlines.body' },
  { icon: '✅', title: 'home.first.tasks', body: 'home.first.tasks.body' },
  { icon: '🏛', title: 'home.first.institutions', body: 'home.first.institutions.body' },
]

export function FirstRun({
  name,
  country,
  flag,
}: {
  name: string | undefined
  country: string
  flag: string
}) {
  const t = useTranslate()
  const navigate = useNavigate()

  return (
    <Stack gap="md">
      <Stack as="header" direction="row" gap="md">
        <span className="avatar" aria-hidden="true">
          {(name ?? country).slice(0, 1)}
        </span>
        <Stack gap="xs">
          <Text as="h1" role="heading-2">
            {name ? t('home.welcome', { name }) : t('home.welcome.plain')}
          </Text>
          <Text role="caption" tone="muted">
            {flag} {country} · {t('home.first.status')}
          </Text>
        </Stack>
      </Stack>

      <Card inverse>
        <Icon size="lg">🪪</Icon>
        <Stack gap="sm">
          <Text as="h2" role="heading-3">
            {t('home.first.title')}
          </Text>
          <Text tone="muted" measure>
            {t('home.first.body')}
          </Text>
        </Stack>
        <Button full variant="quiet" onClick={() => void navigate(routes.documents)}>
          {t('home.first.cta')}
        </Button>
      </Card>

      <Card>
        <Text as="h2" role="eyebrow" tone="muted">
          {t('home.first.next')}
        </Text>
        <Stack as="ul" gap="sm">
          {NEXT.map((hint) => (
            <Stack as="li" key={hint.title} direction="row" gap="sm">
              <Icon size="md">{hint.icon}</Icon>
              <Stack gap="xs">
                <Text as="span" role="body-strong" tone="muted">
                  {t(hint.title)}
                </Text>
                <Text as="span" role="caption" tone="muted">
                  {t(hint.body)}
                </Text>
              </Stack>
            </Stack>
          ))}
        </Stack>
      </Card>

      <Card to={routes.chat}>
        <Stack direction="row" gap="sm">
          <Icon size="md">✨</Icon>
          <Stack gap="xs">
            <Text as="span" role="body-strong">
              {t('home.first.assistant')}
            </Text>
            <Text as="span" role="caption" tone="muted">
              {t('home.first.assistant.body', { country })}
            </Text>
          </Stack>
        </Stack>
      </Card>
    </Stack>
  )
}
